import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Tag, Calendar, Clock, Megaphone, Scissors, BarChart2, TrendingUp, DollarSign, CalendarCheck } from "lucide-react";
import { formatEligibleWeekdays, isPromotionEligibleToday, WEEKDAY_LABELS_PT_BR } from "../../../../shared/promotionWeeklyOffer";

type Promotion = {
  id: number;
  title: string;
  description: string;
  discountText: string | null;
  conditions: string | null;
  startsAt: Date;
  endsAt: Date;
  isActive: boolean;
  serviceIds: string | null;
  eligibleWeekdays: string | null;
  minimumParticipants: number;
  offerPrice: string | null;
  createdAt: Date;
};

type Service = {
  id: number;
  name: string;
  isActive: boolean;
};

type FormData = {
  title: string;
  description: string;
  discountText: string;
  conditions: string;
  startsAt: string;
  endsAt: string;
  isActive: boolean;
  serviceIds: number[];
  eligibleWeekdays: number[];
  minimumParticipants: string;
  offerPrice: string;
};

const emptyForm: FormData = {
  title: "",
  description: "",
  discountText: "",
  conditions: "",
  startsAt: "",
  endsAt: "",
  isActive: true,
  serviceIds: [],
  eligibleWeekdays: [],
  minimumParticipants: "1",
  offerPrice: "",
};

const PROMOTION_TEMPLATES = [
  {
    id: "reactivation",
    label: "Reativação",
    title: "Uma oportunidade especial para você voltar",
    description: "Preparamos uma condição especial para você retomar seus cuidados. Consulte os serviços participantes e aproveite dentro do período da campanha.",
    discountText: "Oferta de retorno",
    conditions: "Válida durante o período informado e sujeita à disponibilidade de agenda.",
  },
  {
    id: "special-date",
    label: "Data especial",
    title: "Condição especial por tempo limitado",
    description: "Celebre uma data especial com uma condição exclusiva nos serviços participantes. Fale com nossa equipe para escolher o melhor horário.",
    discountText: "Condição especial",
    conditions: "Válida apenas no período da campanha e para os serviços selecionados.",
  },
  {
    id: "last-slots",
    label: "Últimas vagas",
    title: "Últimas vagas disponíveis",
    description: "Ainda temos horários disponíveis para os serviços participantes. Garanta seu atendimento antes que as vagas se encerrem.",
    discountText: "Agenda limitada",
    conditions: "Sujeita à disponibilidade de agenda no momento da reserva.",
  },
] as const;

function toLocalDatetimeInput(date: Date): string {
  const d = new Date(date);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function parseServiceIds(raw: string | null): number[] {
  if (!raw) return [];
  try { return JSON.parse(raw); } catch { return []; }
}

function parseWeekdays(raw: string | null): number[] {
  try {
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.map(Number).filter((day) => Number.isInteger(day) && day >= 0 && day <= 6)
      : [];
  } catch {
    return [];
  }
}

function getStatus(p: Promotion): "active" | "scheduled" | "expired" | "disabled" {
  if (!p.isActive) return "disabled";
  const now = new Date();
  if (new Date(p.endsAt) < now) return "expired";
  if (new Date(p.startsAt) > now) return "scheduled";
  if (!isPromotionEligibleToday(p, now)) return "scheduled";
  return "active";
}

const statusLabel: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  active: { label: "Ativa", variant: "default" },
  scheduled: { label: "Agendada", variant: "secondary" },
  expired: { label: "Expirada", variant: "outline" },
  disabled: { label: "Desativada", variant: "destructive" },
};

export default function AdminPromotions() {
  const utils = trpc.useUtils();
  const { data: promotions = [], isLoading } = (trpc as any).promotions.list.useQuery();
  const { data: services = [] } = (trpc as any).services.listAdmin.useQuery();
  const activeServices: Service[] = (services as Service[]).filter((s) => s.isActive);

  const createMutation = (trpc as any).promotions.create.useMutation({
    onSuccess: () => {
      toast.success("Promoção criada com sucesso!");
      utils.promotions.list.invalidate();
      setShowModal(false);
    },
    onError: (e: any) => toast.error(e.message || "Erro ao criar promoção"),
  });

  const updateMutation = (trpc as any).promotions.update.useMutation({
    onSuccess: () => {
      toast.success("Promoção atualizada!");
      utils.promotions.list.invalidate();
      setShowModal(false);
    },
    onError: (e: any) => toast.error(e.message || "Erro ao atualizar promoção"),
  });

  const deleteMutation = (trpc as any).promotions.delete.useMutation({
    onSuccess: () => {
      toast.success("Promoção excluída.");
      utils.promotions.list.invalidate();
    },
    onError: (e: any) => toast.error(e.message || "Erro ao excluir promoção"),
  });

  const toggleMutation = (trpc as any).promotions.update.useMutation({
    onSuccess: () => utils.promotions.list.invalidate(),
    onError: (e: any) => toast.error(e.message),
  });

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormData>(emptyForm);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState("campaigns");

  function openCreate() {
    setEditingId(null);
    setForm(emptyForm);
    setShowModal(true);
  }

  function openEdit(p: Promotion) {
    setEditingId(p.id);
    setForm({
      title: p.title,
      description: p.description,
      discountText: p.discountText ?? "",
      conditions: p.conditions ?? "",
      startsAt: toLocalDatetimeInput(new Date(p.startsAt)),
      endsAt: toLocalDatetimeInput(new Date(p.endsAt)),
      isActive: p.isActive,
      serviceIds: parseServiceIds(p.serviceIds),
      eligibleWeekdays: parseWeekdays(p.eligibleWeekdays),
      minimumParticipants: String(p.minimumParticipants ?? 1),
      offerPrice: p.offerPrice ? String(p.offerPrice) : "",
    });
    setShowModal(true);
  }

  function toggleService(id: number) {
    setForm((prev) => ({
      ...prev,
      serviceIds: prev.serviceIds.includes(id)
        ? prev.serviceIds.filter((s) => s !== id)
        : [...prev.serviceIds, id],
    }));
  }

  function toggleWeekday(day: number) {
    setForm((prev) => ({
      ...prev,
      eligibleWeekdays: prev.eligibleWeekdays.includes(day)
        ? prev.eligibleWeekdays.filter((currentDay) => currentDay !== day)
        : [...prev.eligibleWeekdays, day].sort((a, b) => a - b),
    }));
  }

  function applyPromotionTemplate(template: (typeof PROMOTION_TEMPLATES)[number]) {
    setForm((current) => ({
      ...current,
      title: template.title,
      description: template.description,
      discountText: template.discountText,
      conditions: template.conditions,
    }));
    toast.success(`Modelo “${template.label}” aplicado. Ajuste período e serviços antes de salvar.`);
  }

  function handleSubmit() {
    if (!form.title.trim()) return toast.error("Título é obrigatório.");
    if (!form.description.trim()) return toast.error("Descrição é obrigatória.");
    if (!form.startsAt) return toast.error("Data de início é obrigatória.");
    if (!form.endsAt) return toast.error("Data de fim é obrigatória.");

    const startsAt = new Date(form.startsAt);
    const endsAt = new Date(form.endsAt);
    if (endsAt <= startsAt) return toast.error("A data de fim deve ser posterior à data de início.");
    const minimumParticipants = Number(form.minimumParticipants || "1");
    if (!Number.isInteger(minimumParticipants) || minimumParticipants < 1) {
      return toast.error("Informe a quantidade mínima de participantes.");
    }
    const offerPrice = form.offerPrice.trim() ? Number(form.offerPrice.replace(",", ".")) : undefined;
    if (offerPrice !== undefined && (!Number.isFinite(offerPrice) || offerPrice < 0)) {
      return toast.error("Informe um preço especial válido.");
    }

    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      discountText: form.discountText.trim() || undefined,
      conditions: form.conditions.trim() || undefined,
      startsAt,
      endsAt,
      isActive: form.isActive,
      serviceIds: form.serviceIds.length > 0 ? form.serviceIds : [],
      eligibleWeekdays: form.eligibleWeekdays,
      minimumParticipants,
      offerPrice,
    };

    if (editingId !== null) {
      updateMutation.mutate({ id: editingId, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  const activeCount = promotions.filter((p: Promotion) => getStatus(p) === "active").length;
  const scheduledCount = promotions.filter((p: Promotion) => getStatus(p) === "scheduled").length;

  function getServiceNames(raw: string | null): string[] {
    const ids = parseServiceIds(raw);
    if (ids.length === 0) return [];
    return ids
      .map((id: number) => activeServices.find((s) => s.id === id)?.name)
      .filter(Boolean) as string[];
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-primary" />
            Promoções Sazonais
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Crie campanhas com período de vigência. O agente de IA consulta automaticamente as promoções ativas ao atender clientes.
          </p>
        </div>
        <Button onClick={openCreate} className="gap-2">
          <Plus className="w-4 h-4" />
          Nova Promoção
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* Mobile: caixa suspensa */}
        <div className="block md:hidden mb-2">
          <Select value={activeTab} onValueChange={setActiveTab}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Selecione a seção" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="campaigns">Campanhas</SelectItem>
              <SelectItem value="report">Relatório de Conversões</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {/* Desktop: abas normais */}
        <TabsList className="hidden md:inline-flex mb-4">
          <TabsTrigger value="campaigns" className="gap-2">
            <Megaphone className="w-4 h-4" />
            Campanhas
          </TabsTrigger>
          <TabsTrigger value="report" className="gap-2">
            <BarChart2 className="w-4 h-4" />
            Relatório de Conversões
          </TabsTrigger>
        </TabsList>

        <TabsContent value="campaigns">

      {/* Resumo */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="text-2xl font-bold text-green-600">{activeCount}</div>
            <div className="text-sm text-muted-foreground">Ativas agora</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="text-2xl font-bold text-blue-600">{scheduledCount}</div>
            <div className="text-sm text-muted-foreground">Agendadas</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="text-2xl font-bold">{promotions.length}</div>
            <div className="text-sm text-muted-foreground">Total de campanhas</div>
          </CardContent>
        </Card>
      </div>

      {/* Lista */}
      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">Carregando...</div>
      ) : promotions.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Megaphone className="w-12 h-12 mx-auto text-muted-foreground/40 mb-4" />
            <p className="text-muted-foreground font-medium">Nenhuma promoção cadastrada</p>
            <p className="text-sm text-muted-foreground mt-1">
              Crie sua primeira campanha e o agente de IA vai oferecer automaticamente aos clientes.
            </p>
            <Button onClick={openCreate} className="mt-4 gap-2">
              <Plus className="w-4 h-4" />
              Criar primeira promoção
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {promotions.map((p: Promotion) => {
            const status = getStatus(p);
            const { label, variant } = statusLabel[status];
            const linkedServices = getServiceNames(p.serviceIds);
            const weeklyDays = formatEligibleWeekdays(p.eligibleWeekdays);
            return (
              <Card key={p.id} className={status === "expired" || status === "disabled" ? "opacity-60" : ""}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-base">{p.title}</span>
                        <Badge variant={variant}>{label}</Badge>
                        {p.discountText && (
                          <Badge variant="outline" className="gap-1 text-orange-600 border-orange-300">
                            <Tag className="w-3 h-3" />
                            {p.discountText}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{p.description}</p>
                      {p.conditions && (
                        <p className="text-xs text-muted-foreground mt-1 italic">⚠ {p.conditions}</p>
                      )}
                      {(weeklyDays || Number(p.minimumParticipants) > 1) && (
                        <p className="text-xs text-primary mt-1 font-medium">
                          {weeklyDays ? `Válida: ${weeklyDays}` : ""}
                          {weeklyDays && Number(p.minimumParticipants) > 1 ? " · " : ""}
                          {Number(p.minimumParticipants) > 1 ? `A partir de ${p.minimumParticipants} pessoas` : ""}
                        </p>
                      )}
                      {/* Serviços vinculados */}
                      {linkedServices.length > 0 ? (
                        <div className="flex items-center gap-1 flex-wrap mt-2">
                          <Scissors className="w-3 h-3 text-muted-foreground shrink-0" />
                          {linkedServices.map((name) => (
                            <Badge key={name} variant="secondary" className="text-xs py-0">
                              {name}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                          <Scissors className="w-3 h-3" />
                          Válida para todos os serviços
                        </p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(p.startsAt).toLocaleDateString("pt-BR")}
                        </span>
                        <span>→</span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(p.endsAt).toLocaleDateString("pt-BR")}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Switch
                        checked={p.isActive}
                        onCheckedChange={(val) => toggleMutation.mutate({ id: p.id, isActive: val })}
                        title={p.isActive ? "Desativar" : "Ativar"}
                      />
                      <Button variant="ghost" size="icon" onClick={() => openEdit(p)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setConfirmDeleteId(p.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

         </TabsContent>

        {/* Aba: Relatório de Conversões */}
        <TabsContent value="report">
          <ConversionReport />
        </TabsContent>
      </Tabs>

      {/* Modal Criar/Editar */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar Promoção" : "Nova Promoção"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {!editingId && (
              <div className="space-y-2 rounded-lg border border-primary/15 bg-primary/5 p-3">
                <Label className="text-xs">Começar com um modelo</Label>
                <div className="flex flex-wrap gap-2">
                  {PROMOTION_TEMPLATES.map((template) => (
                    <Button
                      key={template.id}
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-8 bg-background text-xs"
                      onClick={() => applyPromotionTemplate(template)}
                    >
                      {template.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-1">
              <Label>Título da campanha *</Label>
              <Input
                placeholder="Ex: Semana do Bronzeamento"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label>Descrição completa *</Label>
              <Textarea
                placeholder="Descreva a promoção em detalhes. O agente vai usar este texto para explicar ao cliente."
                rows={4}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Este texto é lido pelo agente de IA. Seja claro e detalhado.
              </p>
            </div>
            <div className="space-y-1">
              <Label>Desconto / Oferta</Label>
              <Input
                placeholder="Ex: 20% OFF, R$30 de desconto, Leve 2 pague 1"
                value={form.discountText}
                onChange={(e) => setForm({ ...form, discountText: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label>Condições</Label>
              <Input
                placeholder="Ex: Válido de seg a sex, apenas para novos clientes"
                value={form.conditions}
                onChange={(e) => setForm({ ...form, conditions: e.target.value })}
              />
            </div>
            <div className="space-y-2 rounded-lg border bg-muted/20 p-3">
              <Label>Dias em que a campanha vale</Label>
              <p className="text-xs text-muted-foreground">
                Sem seleção, a campanha vale todos os dias dentro do período. Para o Bronze das amigas, marque apenas segunda-feira.
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {WEEKDAY_LABELS_PT_BR.map((dayLabel, day) => (
                  <label key={dayLabel} className="flex min-h-9 items-center gap-2 rounded border bg-background px-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={form.eligibleWeekdays.includes(day)}
                      onCheckedChange={() => toggleWeekday(day)}
                      aria-label={`Promoção válida ${dayLabel}`}
                    />
                    <span className="capitalize">{dayLabel}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Participantes mínimos</Label>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  inputMode="numeric"
                  value={form.minimumParticipants}
                  onChange={(e) => setForm({ ...form, minimumParticipants: e.target.value })}
                  aria-describedby="promotion-participants-help"
                />
                <p id="promotion-participants-help" className="text-xs text-muted-foreground">Ex.: 2 para uma oferta válida para duas amigas.</p>
              </div>
              <div className="space-y-1">
                <Label>Preço especial por pessoa (R$)</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  placeholder="Ex.: 50,00"
                  value={form.offerPrice}
                  onChange={(e) => setForm({ ...form, offerPrice: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">A oferta é apresentada pela IA; confirme o valor no atendimento/PDV.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Início da campanha *</Label>
                <Input
                  type="datetime-local"
                  value={form.startsAt}
                  onChange={(e) => setForm({ ...form, startsAt: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label>Fim da campanha *</Label>
                <Input
                  type="datetime-local"
                  value={form.endsAt}
                  onChange={(e) => setForm({ ...form, endsAt: e.target.value })}
                />
              </div>
            </div>

            {/* Serviços vinculados */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Scissors className="w-4 h-4" />
                Serviços relacionados
              </Label>
              <p className="text-xs text-muted-foreground">
                Deixe em branco para aplicar a todos os serviços. Selecione serviços específicos para que o agente mencione a promoção apenas quando o cliente perguntar sobre eles.
              </p>
              {activeServices.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">Nenhum serviço ativo encontrado.</p>
              ) : (
                <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                  {activeServices.map((s) => (
                    <div key={s.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`svc-${s.id}`}
                        checked={form.serviceIds.includes(s.id)}
                        onCheckedChange={() => toggleService(s.id)}
                      />
                      <label htmlFor={`svc-${s.id}`} className="text-sm cursor-pointer select-none">
                        {s.name}
                      </label>
                    </div>
                  ))}
                </div>
              )}
              {form.serviceIds.length > 0 && (
                <p className="text-xs text-primary font-medium">
                  {form.serviceIds.length} serviço{form.serviceIds.length > 1 ? "s" : ""} selecionado{form.serviceIds.length > 1 ? "s" : ""}
                </p>
              )}
            </div>

            <div className="flex items-center gap-3">
              <Switch
                checked={form.isActive}
                onCheckedChange={(val) => setForm({ ...form, isActive: val })}
              />
              <Label className="cursor-pointer">Promoção ativa</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
            <Button
              onClick={handleSubmit}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {editingId ? "Salvar alterações" : "Criar promoção"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmar exclusão */}
      <Dialog open={confirmDeleteId !== null} onOpenChange={() => setConfirmDeleteId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Excluir promoção?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Esta ação não pode ser desfeita. A promoção será removida permanentemente.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeleteId(null)}>Cancelar</Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => {
                if (confirmDeleteId !== null) {
                  deleteMutation.mutate({ id: confirmDeleteId });
                  setConfirmDeleteId(null);
                }
              }}
            >
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

// ─── Componente de Relatório de Conversões ──────────────────────────────────────

function ConversionReport() {
  const { data: report = [], isLoading } = (trpc as any).promotions.conversionReport.useQuery();

  const totalConversions = report.reduce((acc: number, p: any) => acc + (p.conversions ?? 0), 0);
  const totalRevenue = report.reduce((acc: number, p: any) => acc + (p.revenue ?? 0), 0);

  function getStatus(p: any): "active" | "scheduled" | "expired" | "disabled" {
    if (!p.isActive) return "disabled";
    const now = new Date();
    if (new Date(p.endsAt) < now) return "expired";
    if (new Date(p.startsAt) > now) return "scheduled";
    if (!isPromotionEligibleToday(p, now)) return "scheduled";
    return "active";
  }

  const statusLabel: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
    active: { label: "Ativa", variant: "default" },
    scheduled: { label: "Agendada", variant: "secondary" },
    expired: { label: "Expirada", variant: "outline" },
    disabled: { label: "Desativada", variant: "destructive" },
  };

  if (isLoading) {
    return <div className="text-center py-12 text-muted-foreground">Carregando relatório...</div>;
  }

  if (report.length === 0) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <BarChart2 className="w-12 h-12 mx-auto text-muted-foreground/40 mb-4" />
          <p className="text-muted-foreground font-medium">Nenhuma promoção cadastrada ainda</p>
          <p className="text-sm text-muted-foreground mt-1">
            Crie promoções na aba Campanhas para ver o relatório de conversões.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Resumo geral */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1">
              <CalendarCheck className="w-4 h-4 text-primary" />
              <span className="text-xs text-muted-foreground">Total de Agendamentos</span>
            </div>
            <div className="text-2xl font-bold">{totalConversions}</div>
            <div className="text-xs text-muted-foreground">durante períodos de promoção</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-4 h-4 text-green-600" />
              <span className="text-xs text-muted-foreground">Receita no Período</span>
            </div>
            <div className="text-2xl font-bold text-green-600">
              R$ {totalRevenue.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="text-xs text-muted-foreground">soma de todas as campanhas</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-blue-600" />
              <span className="text-xs text-muted-foreground">Campanhas</span>
            </div>
            <div className="text-2xl font-bold">{report.length}</div>
            <div className="text-xs text-muted-foreground">total cadastradas</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabela de conversões */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-secondary/30">
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Campanha</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Período</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Agendamentos</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Receita</th>
                </tr>
              </thead>
              <tbody>
                {report.map((p: any) => {
                  const status = getStatus(p);
                  const { label, variant } = statusLabel[status];
                  return (
                    <tr key={p.id} className="border-b last:border-0 hover:bg-secondary/10">
                      <td className="px-4 py-3">
                        <div className="font-medium">{p.title}</div>
                        {p.discountText && (
                          <div className="text-xs text-orange-600 flex items-center gap-1 mt-0.5">
                            <Tag className="w-3 h-3" />
                            {p.discountText}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={variant} className="text-xs">{label}</Badge>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        <div className="flex items-center gap-1 text-xs">
                          <Calendar className="w-3 h-3" />
                          {new Date(p.startsAt).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })}
                          {" → "}
                          {new Date(p.endsAt).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-semibold ${p.conversions > 0 ? "text-primary" : "text-muted-foreground"}`}>
                          {p.conversions ?? 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-semibold ${p.revenue > 0 ? "text-green-600" : "text-muted-foreground"}`}>
                          R$ {(p.revenue ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        * Os agendamentos e receita são contabilizados com base nos que foram criados durante o período de vigência de cada campanha (data de início até data de fim), independentemente do canal de origem.
      </p>
    </div>
  );
}
